import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet, API_BASE } from '../lib/api';
import { PageTitle, Card, Btn, EmptyState, Section, Kpi, Pill, fmt } from '../components/ui-kit';
import { Lock, Download } from 'lucide-react';

interface DocFile { id: string; name: string; size: number; encrypted: boolean; created_at: string; }

export default function MissionDocs() {
  const { id } = useParams();
  const { getToken } = useXyqarAuth();
  const [items, setItems] = React.useState<DocFile[] | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const load = React.useCallback(async () => {
    try {
      const tok = await getToken().catch(() => '');
      const j = await apiGet<{ documents?: DocFile[]; docs?: DocFile[] }>(`/api/v1/client/missions/${id}/docs`, tok);
      setItems(j.documents ?? j.docs ?? []);
    } catch { setItems([]); }
  }, [id, getToken]);

  React.useEffect(() => { load(); }, [load]);

  const upload = async (file: File) => {
    // E2E: encrypt with libsodium WASM (sealed_box) before upload — placeholder
    // For now we send raw bytes; server-side at-rest encryption applies.
    // TODO: integrate @xyqar/shared-crypto sealedBox once WASM bundle is wired.
    const fd = new FormData();
    fd.append('file', file);
    const tok = await getToken().catch(() => '');
    await fetch(`${API_BASE}/api/v1/client/missions/${id}/docs`, {
      method: 'POST',
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      body: fd,
    });
    load();
  };

  const encryptedCount = (items ?? []).filter((file) => file.encrypted).length;
  const totalSizeKb = (items ?? []).reduce((sum, file) => sum + (file.size ?? 0), 0) / 1024;

  return (
    <div>
      <PageTitle kicker={`Mission · ${id}`} title="Mission documents" />

      <Section title="At a glance" hint="Document state reflects API metadata returned for this mission only.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="Files" value={fmt(items?.length)} />
          <Kpi accent="green" label="Encrypted flag" value={fmt(encryptedCount)} />
          <Kpi label="Stored only" value={fmt(Math.max((items?.length ?? 0) - encryptedCount, 0))} />
          <Kpi accent="blue" label="Total size" value={`${totalSizeKb.toFixed(1)} KB`} />
        </div>
      </Section>

      <Section title="Upload channel">
        <Card className="mb-6">
          <div className="flex items-center gap-4">
            <Lock className="h-5 w-5 text-green-400" />
            <div className="flex-1">
              <p className="text-sm text-white">Authenticated document intake with server-side storage controls</p>
              <p className="text-xs text-white/40 mt-1">
                Browser-side sealed-box encryption is not enabled yet. Files are uploaded over authenticated transport;
                per-file encrypted storage should only be considered active when the API marks the file as encrypted.
              </p>
            </div>
            <input ref={fileRef} type="file" hidden onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
            <Btn primary onClick={() => fileRef.current?.click()}>+ Upload</Btn>
          </div>
        </Card>
      </Section>

      <Section title="Document ledger">
        {items === null && <Card>Loading…</Card>}
        {items && items.length === 0 && <EmptyState title="No documents yet" hint="Upload statements of work, annexes, or mission evidence for this mandate." />}
        {items && items.length > 0 && (
          <Card>
            <div className="flex flex-col divide-y divide-white/5">
              {items.map((d) => (
                <div key={d.id} className="flex items-center justify-between py-3 gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <Lock className="h-4 w-4 text-green-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{d.name}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-[10px] text-white/40 font-mono">{(d.size / 1024).toFixed(1)} KB</span>
                        <span className="text-[10px] text-white/40 font-mono">{d.created_at?.slice(0, 10) ?? '—'}</span>
                        <Pill tone={d.encrypted ? 'live' : 'warn'}>{d.encrypted ? 'encrypted' : 'stored'}</Pill>
                      </div>
                    </div>
                  </div>
                  <a href={`${API_BASE}/api/v1/client/missions/${id}/docs/${d.id}`} className="text-white/60 hover:text-white text-xs flex items-center gap-1">
                    <Download className="h-4 w-4" /> Download
                  </a>
                </div>
              ))}
            </div>
          </Card>
        )}
      </Section>
    </div>
  );
}
